import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FilterListIcon from '@mui/icons-material/FilterList';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarningIcon from '@mui/icons-material/Warning';
import queryString from 'query-string';
import { CreateShippingRateRequest } from '../../common/model/dto/shipping-rate/create-shipping-rate-request';
import { OrderBy } from '../../common/types';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import { getListRange } from '../../common/utils/services/order-list-utils';
import { SHIPPING_RATE_LIST_FILTERS } from '../../common/model/ui/shipping-rate-list-filters';
import { useAdminShippingRateMutations } from '../../hooks/use-admin-shipping-rate-mutations';
import { useListQueryParams } from '../../hooks/use-list-query-params';
import { getAdminShippingRates } from '../../services/admin/admin-shipping-rate.service';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvCreateShippingRateDialog from '../UI/molecules/OvCreateShippingRateDialog';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvListFilterBar from '../UI/organisms/OvListFilterBar';
import OvShippingRateList from '../UI/organisms/OvShippingRateList';
import OvRecordListPage from '../UI/organisms/OvRecordListPage';

export const SHIPPING_RATE_LIST_LIMIT = 10;

const DEFAULT_ORDER: OrderBy = {
  order_by: 'created_at',
  order_dir: 'desc',
};

const AdminShippingRateList: FC = () => {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    offset,
    orderBy,
    filters,
    filterQueryParams,
    hasAppliedFilters,
    changeFilter,
    clearFilters,
    setOffset,
  } = useListQueryParams(SHIPPING_RATE_LIST_FILTERS, DEFAULT_ORDER);

  const query = queryString.stringify({
    limit: SHIPPING_RATE_LIST_LIMIT,
    offset,
    order_by: orderBy?.order_by,
    order_dir: orderBy?.order_dir,
    ...filterQueryParams,
  });

  const {
    data: paginatedRecords,
    isPending,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['admin-shipping-rates', query],
    queryFn: () => getAdminShippingRates(query),
    placeholderData: keepPreviousData,
  });

  const records = paginatedRecords?.docs ?? [];
  const total = paginatedRecords?.total ?? 0;

  const { createRate } = useAdminShippingRateMutations();

  const handleOnSave = (request: CreateShippingRateRequest) => {
    setIsDialogOpen(false);
    createRate.mutate(request);
  };

  const getResultSummary = () => {
    if (isError) {
      return t('common.listResults.unavailable');
    }

    if (isPending) {
      return t('common.listResults.loading');
    }

    return t('shippingRates.resultRange', {
      ...getListRange(offset, SHIPPING_RATE_LIST_LIMIT, total),
      total,
    });
  };

  const renderContent = () => {
    if (isError) {
      return (
        <OvListStateMessage
          isAlert
          icon={<WarningIcon />}
          title={t('shippingRates.states.errorTitle')}
          description={t('shippingRates.states.errorDescription')}
        >
          <OvCompactButton onClick={() => refetch()}>
            {t('common.actions.retry')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    if (isPending) {
      return null;
    }

    if (records.length > 0) {
      return <OvShippingRateList shippingRates={records} />;
    }

    if (hasAppliedFilters) {
      return (
        <OvListStateMessage
          icon={<FilterListIcon />}
          title={t('shippingRates.states.noResultsTitle')}
          description={t('shippingRates.states.noResultsDescription')}
        >
          <OvCompactButton onClick={clearFilters}>
            {t('common.actions.clearFilters')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    return (
      <OvListStateMessage
        icon={<LocalShippingIcon />}
        title={t('shippingRates.states.emptyTitle')}
        description={t('shippingRates.emptyShippingRateList')}
      />
    );
  };

  return (
    <OvRecordListPage
      title={t('app.subPages.menu.shippingRates')}
      icon={<LocalShippingIcon />}
      headerAction={
        <OvCompactButton
          icon={<AddCircleIcon />}
          onClick={() => setIsDialogOpen(true)}
        >
          {t('common.actions.create')}
        </OvCompactButton>
      }
      filters={
        <OvListFilterBar
          filters={filters}
          onFilterChange={changeFilter}
          onClearFilters={clearFilters}
        />
      }
      context={
        createRate.error && (
          <OvErrorMessage message={getAxiosErrorMessage(createRate.error)} />
        )
      }
      resultSummary={getResultSummary()}
      total={total}
      limit={SHIPPING_RATE_LIST_LIMIT}
      offset={offset}
      onPageChange={(_, page) =>
        setOffset((page - 1) * SHIPPING_RATE_LIST_LIMIT)
      }
      isBusy={isFetching || createRate.isPending}
    >
      {renderContent()}

      <OvCreateShippingRateDialog
        isOpen={isDialogOpen}
        onCancel={() => setIsDialogOpen(false)}
        onSave={handleOnSave}
      />
    </OvRecordListPage>
  );
};

export default AdminShippingRateList;
